import { OffersWarrantyType, PAGES } from "@dune/types";
import {
	Button,
	Card,
	CardContent,
	Input,
	Label,
	Loader,
	RadioGroup,
	RadioGroupItem,
} from "@dune/ui";
import { formatCurrency, isOfferWarrantyType } from "@dune/utils";
import { useEffect, useState } from "react";
import { useUpsertEndorsementRenewalPricingsFromReception } from "../../../../api/mutations/constructionSiteReception.mutations";
import {
	useEvaluateEndorsementRenewalPricing,
	useGetConstructionSiteDetails,
} from "../../../../api/queries/constructionSite.queries";
import { useGetConstructionSiteSubmission } from "../../../../api/queries/constructionSiteSubmission.queries";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { useValidateReceptionAnalysis } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { useNavigate } from "react-router-dom";
import { usePricingOptions } from "../../offers/contexts/PricingOptionsContext";

type Props = { onPrev: () => void };

const SelectedBinderWarrantyItem = ({
	selectedWarrantyId,
	warrantyType,
	warrantyName,
	companyName,
	binderAmendmentId,
	constructionSiteId,
	finalTotalAmount,
	onCreateReceiptChange,
}: {
	selectedWarrantyId: number;
	warrantyType: OffersWarrantyType | null;
	warrantyName: string | null;
	companyName: string | null;
	binderAmendmentId: number;
	constructionSiteId: string;
	finalTotalAmount: number;
	onCreateReceiptChange: (id: number, createReceipt: boolean) => void;
}) => {
	const [createReceipt, setCreateReceipt] = useState(true);

	const handleCreateReceiptChange = (v: string) => {
		const newValue = v === "yes";
		setCreateReceipt(newValue);
		onCreateReceiptChange(selectedWarrantyId, newValue);
	};

	const { getPricingOptionsForWarranty } = usePricingOptions();
	const pricingOptions = getPricingOptionsForWarranty(selectedWarrantyId);

	const { data: pricingResult, isLoading } =
		useEvaluateEndorsementRenewalPricing(
			constructionSiteId,
			warrantyType,
			binderAmendmentId,
			{
				finalConstructionCost: finalTotalAmount,
				brokerFees: pricingOptions.brokerFees,
			},
		);

	const warningLabel = `${warrantyName || warrantyType || "Garantie"} - ${companyName || "Assureur"}`;

	if (isLoading) {
		return (
			<Card className="py-0 mb-4">
				<CardContent className="flex flex-col gap-8 p-6">
					<div className="flex items-center justify-center py-12">
						<Loader />
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="py-0 mb-4">
			<CardContent className="flex flex-col gap-8 p-6">
				<h3 className="text-lg font-semibold text-foreground">
					{warningLabel}
				</h3>

				<div className="grid grid-cols-[280px_1fr] items-center gap-6">
					<Label className="text-foreground whitespace-nowrap text-sm md:text-base font-medium">
						Prime additionnelle TTC
					</Label>
					<Input
						value={
							pricingResult?.totalClientPrimeTTC === undefined
								? "..."
								: formatCurrency(pricingResult?.totalClientPrimeTTC)
						}
						readOnly
					/>
				</div>

				<div className="grid grid-cols-[280px_1fr] items-center gap-6">
					<Label className="text-foreground whitespace-nowrap text-sm md:text-base font-medium">
						Créer la quittance de réception
					</Label>
					<RadioGroup
						className="flex flex-row gap-8"
						value={createReceipt ? "yes" : "no"}
						onValueChange={handleCreateReceiptChange}
					>
						<div className="flex items-center gap-3">
							<RadioGroupItem
								id={`receipt-yes-${selectedWarrantyId}`}
								value="yes"
							/>
							<Label
								htmlFor={`receipt-yes-${selectedWarrantyId}`}
								className="text-foreground text-sm"
							>
								Oui
							</Label>
						</div>
						<div className="flex items-center gap-3">
							<RadioGroupItem
								id={`receipt-no-${selectedWarrantyId}`}
								value="no"
							/>
							<Label
								htmlFor={`receipt-no-${selectedWarrantyId}`}
								className="text-foreground text-sm"
							>
								Non
							</Label>
						</div>
					</RadioGroup>
				</div>
			</CardContent>
		</Card>
	);
};

export const ReceptionAnalysisValidation = ({ onPrev }: Props) => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const navigate = useNavigate();

	const [validated, setValidated] = useState(true);
	const [selectedWarrantiesData, setSelectedWarrantiesData] = useState<
		Record<number, { createReceipt: boolean }>
	>({});

	const handleCreateReceiptChange = (
		warrantyId: number,
		createReceipt: boolean,
	) => {
		setSelectedWarrantiesData((prev) => ({
			...prev,
			[warrantyId]: {
				...prev[warrantyId],
				createReceipt,
			},
		}));
	};

	const { mutateAsync: validateReceptionAnalysis, isPending: isSubmitting } =
		useValidateReceptionAnalysis();
	const { mutateAsync: upsertPricings, isPending: isPricingSubmitting } =
		useUpsertEndorsementRenewalPricingsFromReception();

	const isDataLoading = isPricingSubmitting || isSubmitting;

	const { data: constructionSiteDetails } =
		useGetConstructionSiteDetails(constructionSiteId);
	const { data: constructionSiteSubmission } = useGetConstructionSiteSubmission(
		Number(constructionSiteId),
	);

	const selectedBinderWarranties =
		constructionSiteSubmission?.selectedBinderWarranties ?? [];
	const finalTotalAmount =
		constructionSiteDetails?.constructionSiteDetails?.finalTotalAmount ?? 0;

	const filteredSelectedBinderWarranties = selectedBinderWarranties.filter(
		(warranty) => {
			const propositions = warranty.binderWarranty.binderAmendment.propositions;
			const binderWarranty = warranty.binderWarranty;
			return propositions.some(
				(proposition) =>
					proposition.status === "CONTRACT_ONGOING" &&
					proposition.productType === binderWarranty.warrantyType,
			);
		},
	);

	useEffect(() => {
		if (filteredSelectedBinderWarranties.length > 0) {
			const initialData: Record<number, { createReceipt: boolean }> = {};

			for (const selectedWarranty of filteredSelectedBinderWarranties) {
				const warrantyId = selectedWarranty.id;
				if (!(warrantyId in selectedWarrantiesData)) {
					initialData[warrantyId] = {
						createReceipt: true,
					};
				}
			}

			if (Object.keys(initialData).length > 0) {
				setSelectedWarrantiesData((prev) => ({
					...prev,
					...initialData,
				}));
			}
		}
	}, [filteredSelectedBinderWarranties, selectedWarrantiesData]);

	const savePricings = async () => {
		const pricingsToSave = filteredSelectedBinderWarranties.map((warranty) => ({
			binderWarrantyId: warranty.binderWarranty.id,
		}));

		if (pricingsToSave.length > 0) {
			await upsertPricings({
				constructionSiteId: Number(constructionSiteId),
				pricings: pricingsToSave,
			});
		}
	};

	const handleValidate = async () => {
		await savePricings();

		await validateReceptionAnalysis({
			constructionSiteId: Number(constructionSiteId),
			data: {
				addReservesEnabled: false,
				selectedWarrantiesData,
			},
		});

		navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
	};

	const handleSaveAndClose = async () => {
		await savePricings();
		navigate(PAGES.PROJECTS.show.withParams(constructionSiteId));
	};

	return (
		<div className="flex flex-col gap-6 p-6">
			<h2 className="text-2xl font-medium text-text-secondary-blue">
				Validation
			</h2>
			<Card className="py-0">
				<CardContent className="flex flex-col gap-8 p-6">
					<div className="grid grid-cols-[280px_1fr] items-center gap-6">
						<Label className="text-foreground whitespace-nowrap text-sm md:text-base font-medium">
							Valider la réception
						</Label>
						<RadioGroup
							className="flex flex-row gap-8"
							value={validated ? "yes" : "no"}
							onValueChange={(v) => setValidated(v === "yes")}
						>
							<div className="flex items-center gap-3">
								<RadioGroupItem id="reception-yes" value="yes" />
								<Label
									htmlFor="reception-yes"
									className="text-foreground text-sm"
								>
									Oui
								</Label>
							</div>
							<div className="flex items-center gap-3">
								<RadioGroupItem id="reception-no" value="no" />
								<Label
									htmlFor="reception-no"
									className="text-foreground text-sm"
								>
									Non
								</Label>
							</div>
						</RadioGroup>
					</div>
				</CardContent>
			</Card>
			<div>
				<h3 className="text-lg font-semibold text-foreground mb-4">
					Garanties sélectionnées
				</h3>
				{isDataLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader />
					</div>
				) : (
					filteredSelectedBinderWarranties.map((selectedWarranty) => {
						const binderWarranty = selectedWarranty.binderWarranty;
						const binderAmendment = binderWarranty.binderAmendment;

						const insurerCompanyName =
							binderAmendment.binder.insurer.society.companyName;

						return (
							<SelectedBinderWarrantyItem
								key={selectedWarranty.id}
								selectedWarrantyId={selectedWarranty.id}
								warrantyType={
									isOfferWarrantyType(binderWarranty.warrantyType)
										? binderWarranty.warrantyType
										: null
								}
								warrantyName={binderWarranty.name}
								companyName={insurerCompanyName}
								binderAmendmentId={binderAmendment.id}
								constructionSiteId={constructionSiteId}
								finalTotalAmount={finalTotalAmount}
								onCreateReceiptChange={handleCreateReceiptChange}
							/>
						);
					})
				)}
			</div>{" "}
			<div className="flex flex-row h-11 items-center justify-between w-full mt-4">
				<Button variant="outline" onClick={onPrev}>
					Précédent
				</Button>
				<div className="flex flex-row gap-4 items-center">
					<Button
						variant="outline"
						onClick={handleSaveAndClose}
						disabled={isSubmitting || isPricingSubmitting}
						isLoading={isPricingSubmitting}
					>
						Enregistrer et fermer
					</Button>
					<Button
						onClick={handleValidate}
						disabled={isSubmitting || isPricingSubmitting || !validated}
						isLoading={isSubmitting}
					>
						Valider
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReceptionAnalysisValidation;
